import { notesApi, calendarsApi, eventsApi, breaksApi } from './cloudApi';

const DEFAULT_SYNC_INTERVAL = 30 * 1000; // 30 saniye - sadece periyodik sync için

let periodicSyncTimer = null;

const isNavigatorOnline = () => {
  if (typeof navigator === 'undefined' || typeof navigator.onLine === 'undefined') {
    return true;
  }
  return navigator.onLine;
};


const normalizeRecords = (records) => {
  if (!Array.isArray(records)) {
    return [];
  }
  return records.map((record) => ({
    ...record,
    createdAt: record.created_at || record.createdAt,
    updatedAt: record.updated_at || record.updatedAt,
  }));
};

const loadAndMerge = async ({ entity, token, since, onRecordsFetched }) => {
  // Note: last_sync is now updated once after all entities are synced (in pullRemoteChanges)
  switch (entity) {
    case 'notes': {
      const response = await notesApi.list({ token, since });
      const list = normalizeRecords(response?.notes || []);
      if (list.length > 0) {
        onRecordsFetched?.('notes', list);
      }
      return list;
    }

    case 'calendars': {
      const response = await calendarsApi.list({ token, since });
      const list = normalizeRecords(response?.calendars || []);
      if (list.length > 0) {
        onRecordsFetched?.('calendars', list);
      }
      return list;
    }

    case 'events': {
      const response = await eventsApi.list({ token, since });
      const list = normalizeRecords(response?.events || []);
      if (list.length > 0) {
        onRecordsFetched?.('events', list);
      }
      return list;
    }

    case 'breaks': {
      const response = await breaksApi.list({ token, since });
      const list = normalizeRecords(response?.breaks || []);
      if (list.length > 0) {
        onRecordsFetched?.('breaks', list);
      }
      return list;
    }

    default:
      return [];
  }
};

export const pullRemoteChanges = async ({ token, entities = ['notes', 'calendars', 'events', 'breaks'], onRecordsFetched, since } = {}) => {
  if (!token) {
    return { status: 'skipped', reason: 'TOKEN_MISSING' };
  }

  if (!isNavigatorOnline()) {
    return { status: 'skipped', reason: 'OFFLINE' };
  }

  // Sayfa görünür değilse sync yapma
  if (typeof document !== 'undefined' && document.hidden) {
    return { status: 'skipped', reason: 'PAGE_HIDDEN' };
  }

  const results = {};

  for (const entity of entities) {
    try {
      const records = await loadAndMerge({ entity, token, since, onRecordsFetched });
      results[entity] = records;
    } catch (error) {
      console.warn('Uzak değişiklikler alınamadı:', entity, error);
    }
  }

  return { status: 'completed', results };
};

let lastSyncCycleTime = 0;
const MIN_SYNC_CYCLE_INTERVAL = 5000; // 5 saniye minimum interval - çok sık sync'i engelle

export const runSyncCycle = async ({ token, onSyncTick, skipPull = false, since } = {}) => {
  if (!token) {
    return { status: 'skipped', reason: 'TOKEN_MISSING' };
  }

  // Sayfa görünür değilse sync yapma
  const isPageHidden = typeof document !== 'undefined' && document.hidden;

  // Çok sık sync cycle'ı engelle
  const now = Date.now();
  if (now - lastSyncCycleTime < MIN_SYNC_CYCLE_INTERVAL) {
    return { status: 'skipped', reason: 'TOO_FREQUENT' };
  }
  lastSyncCycleTime = now;
  
  // Pull işlemini sadece sayfa görünürse ve skipPull false ise yap
  let pullResult = { status: 'skipped', reason: 'SKIP_PULL' };
  if (!skipPull && !isPageHidden) {
    pullResult = await pullRemoteChanges({ token, onRecordsFetched: onSyncTick, since });
  }

  // Sadece gerçekten değişiklik varsa tick event'i gönder
  const hasChanges = 
    (pullResult.results && Object.values(pullResult.results).some(records => Array.isArray(records) && records.length > 0));
  
  if (hasChanges) {
    onSyncTick?.('tick', { pullResult, triggeredAt: new Date().toISOString(), manual: !skipPull });
  }

  return { pullResult };
};

let isSyncing = false;

export const startPeriodicSync = ({ tokenRef, interval = DEFAULT_SYNC_INTERVAL, onSyncTick, since } = {}) => {
  if (periodicSyncTimer) {
    clearInterval(periodicSyncTimer);
  }

  const tick = async () => {
    // Eğer zaten bir sync çalışıyorsa, yeni sync başlatma
    if (isSyncing) {
      return;
    }

    const token = typeof tokenRef === 'function' ? tokenRef() : tokenRef;
    if (!token) {
      return;
    }

    // Sayfa görünür değilse sync yapma (arka planda çalışmayı önle)
    if (typeof document !== 'undefined' && document.hidden) {
      return;
    }

    isSyncing = true;
    try {
      await runSyncCycle({ token, onSyncTick, since });
    } catch (error) {
      console.warn('Sync cycle hatası:', error);
    } finally {
      isSyncing = false;
    }
  };

  // Sadece periyodik sync - 30 saniyede bir
  // İlk sync'i hemen başlatma - kullanıcı giriş yaptığında zaten sync yapılıyor
  // İlk periyodik sync 30 saniye sonra başlasın
  periodicSyncTimer = setInterval(tick, interval);
  
  // Online olduğunda sync yap (internet bağlantısı geri geldiğinde)
  if (typeof window !== 'undefined') {
    window.addEventListener('online', tick);
  }

  return () => {
    if (periodicSyncTimer) {
      clearInterval(periodicSyncTimer);
      periodicSyncTimer = null;
    }
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', tick);
    }
    isSyncing = false;
  };
};

export const resetSyncState = () => {
  // ONLINE-ONLY: No local state to reset
};



